// Refund kind and breakdown amounts come straight from the backend — never inferred from amount vs orderTotal.
import React from "react";
import { createStyles } from "@mantine/core";
import { tokens } from "@blurb/codex-react";
import { Body, Label } from "../../../../components/Typography";
import { BreakdownTable } from "./BreakdownTable";
import { OrderDetailCard, CardSection } from "./OrderDetailCard";
import { PaymentLink } from "./PaymentLink";
import {
  formatOrderDate,
  getMostRecentRefund,
  getRefundKindLabel,
  getRefundReasonDisplay,
  toRefundBreakdown,
  type Order,
} from "./shared";

const useStyles = createStyles(() => ({
  reason: {
    color: tokens.color.primitive.light.gray[700],
  },
}));

export const RefundCard: React.FC<{ order: Order }> = ({ order }) => {
  const { classes } = useStyles();
  const refund = getMostRecentRefund(order.refunds ?? []);
  if (refund === null) return null;

  const breakdown = toRefundBreakdown(refund, order.pricing?.currency);
  const reasonDisplay = getRefundReasonDisplay(refund);

  return (
    <OrderDetailCard>
      <CardSection>
        <Label>Refund</Label>
        <Body>
          {getRefundKindLabel(refund)}
          {refund.createdAt && <> on {formatOrderDate(refund.createdAt)}</>}
          {typeof refund.id === "number" && <PaymentLink paymentId={refund.id} />}
        </Body>
        {reasonDisplay && <Body className={classes.reason}>{reasonDisplay}</Body>}
      </CardSection>

      {breakdown && (
        <BreakdownTable
          data={{
            currency: breakdown.currency,
            itemsTotal: breakdown.itemsTotal,
            shippingTotal: breakdown.shippingTotal,
            taxesTotal: breakdown.taxesTotal,
            total: breakdown.refundTotal,
          }}
          sectionLabel="Total Refund Amount"
          totalLabel="Refund Total"
          prefix="- "
        />
      )}
    </OrderDetailCard>
  );
};
